import { getApiUrl, getRelativeUrl } from "../../utils/urlHelper.js";
import { getBrowserSafeUrl } from "./urlHelpers.js";

export const getWatermarkedImageBlob = (photoUrl, watermarkConfig) => {
  return new Promise((resolve, reject) => {
    const fullUrl = getBrowserSafeUrl(photoUrl);
    const photoImg = new Image();
    photoImg.crossOrigin = "anonymous";
    photoImg.src = fullUrl;

    const fetchFallbackBlob = () => {
      fetch(fullUrl)
        .then((res) => res.blob())
        .then((blob) => resolve(blob))
        .catch(() => reject(new Error("Failed to fetch image")));
    };

    photoImg.onload = () => {
      const isEnabled = watermarkConfig?.enabled !== false;
      const wmImgSrc = watermarkConfig?.image || "/images/logo22.png";

      if (!isEnabled || !wmImgSrc) {
        fetchFallbackBlob();
        return;
      }

      const wmImg = new Image();
      wmImg.crossOrigin = "anonymous";
      wmImg.src = getApiUrl(getRelativeUrl(wmImgSrc));

      wmImg.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = photoImg.naturalWidth || photoImg.width;
          canvas.height = photoImg.naturalHeight || photoImg.height;
          const ctx = canvas.getContext("2d");

          ctx.drawImage(photoImg, 0, 0, canvas.width, canvas.height);

          const opacity = (watermarkConfig.opacity ?? 70) / 100;
          ctx.globalAlpha = opacity;

          const scalePercent = (watermarkConfig.scale ?? 20) / 100;
          const position = watermarkConfig.position || "bottom-right";

          if (position === "tile") {
            const cols = 3;
            const rows = 3;
            const wmWidth = canvas.width * scalePercent * 1.5;
            const wmHeight = (wmImg.height / wmImg.width) * wmWidth;

            for (let r = 0; r < rows; r++) {
              for (let c = 0; c < cols; c++) {
                const x = (canvas.width / cols) * c + (canvas.width / cols - wmWidth) / 2;
                const y = (canvas.height / rows) * r + (canvas.height / rows - wmHeight) / 2;
                ctx.drawImage(wmImg, x, y, wmWidth, wmHeight);
              }
            }
          } else {
            const wmWidth = canvas.width * scalePercent;
            const wmHeight = (wmImg.height / wmImg.width) * wmWidth;

            const posXPercent = (watermarkConfig.posX ?? 85) / 100;
            const posYPercent = (watermarkConfig.posY ?? 85) / 100;

            const x = canvas.width * posXPercent - wmWidth / 2;
            const y = canvas.height * posYPercent - wmHeight / 2;

            ctx.drawImage(wmImg, x, y, wmWidth, wmHeight);
          }

          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else fetchFallbackBlob();
            },
            "image/jpeg",
            0.95
          );
        } catch (e) {
          fetchFallbackBlob();
        }
      };

      wmImg.onerror = () => fetchFallbackBlob();
    };

    photoImg.onerror = () => fetchFallbackBlob();
  });
};

export const downloadSingleImage = async (photoUrl, fileName, watermarkConfig) => {
  try {
    const blob = await getWatermarkedImageBlob(photoUrl, watermarkConfig);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download.jpg";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    window.open(getBrowserSafeUrl(photoUrl), "_blank");
    return true;
  }
};
